import { CommonModule } from '@angular/common'
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core'
import { RouterModule } from '@angular/router'
import * as fastLevenshtein from 'fast-levenshtein'
import { HTMLInputEvent } from '../../interfaces/html-input-event.interface'

export type SearchResult = {
  id: number
  label: string
}

@Component({
  selector: 'app-select-autocomplete-input',
  templateUrl: './select-autocomplete-input.component.html',
  styleUrls: ['./select-autocomplete-input.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule]
})
export class SelectAutocompleteInputComponent implements OnInit {
  @Input() searchFunction: (terms: string) => Promise<SearchResult[]>
  @Input() initialValueFetchFunction: (
    ids: string[] | number[]
  ) => Promise<SearchResult[]>
  @Input() initialValue: number[] | string[]

  @Input() placeholder: string
  @Input() isError: boolean
  @Input() maxSelectedSearchResults = 1

  @Input() noResultContent = 'No result...'
  @Input() noResultLink: string

  @Output() selectionChanged: EventEmitter<number[]> = new EventEmitter()

  @ViewChild('searchInput', { static: true }) searchEl: ElementRef

  showList: boolean
  focusedItemIndex: number
  suggestedSearchResults: SearchResult[] = []
  selectedSearchResults: SearchResult[] = []

  constructor(private elementRef: ElementRef) {}

  async ngOnInit(): Promise<void> {
    if (this.initialValue?.length && this.initialValueFetchFunction) {
      this.selectedSearchResults = await this.initialValueFetchFunction(
        this.initialValue
      )
    }
  }

  onSearchInputKeyup(event: HTMLInputEvent) {
    const terms = event.target.value
    this.showList = true

    // Navigate through results
    if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
      event.preventDefault()
      return this.navigateSuggestedValues(event.key)
    }

    this.searchFunction(terms).then((searchResultsRes: SearchResult[]) => {
      this.suggestedSearchResults = searchResultsRes
        .sort(
          (a, b) =>
            fastLevenshtein.get(terms, a.label) -
            fastLevenshtein.get(terms, b.label)
        )
        .slice(0, 20)
      delete this.focusedItemIndex
    })
  }

  // Use arrowKeys and enter to select suggested themes with keyboard.
  navigateSuggestedValues(key: string): void {
    if (key === 'ArrowDown') {
      if (typeof this.focusedItemIndex === 'undefined') {
        this.showList = true
        this.focusedItemIndex = 0
      } else if (
        this.focusedItemIndex <
        this.suggestedSearchResults.length - 1
      ) {
        this.focusedItemIndex++
      }
    } else if (key === 'ArrowUp') {
      if (!this.focusedItemIndex) {
        this.showList = false
        delete this.focusedItemIndex
      } else {
        this.focusedItemIndex--
      }
    } else if (
      key === 'Enter' &&
      typeof this.focusedItemIndex !== 'undefined' &&
      this.suggestedSearchResults[this.focusedItemIndex]
    ) {
      this.toggleItem(this.suggestedSearchResults[this.focusedItemIndex])
      delete this.focusedItemIndex
    }
  }

  toggleItem(searchResult: SearchResult) {
    if (
      this.selectedSearchResults.find(
        (selectedResult) => selectedResult.id === searchResult.id
      )
    ) {
      this.selectedSearchResults.splice(
        this.selectedSearchResults.findIndex(
          (selectedResult) => selectedResult.id === searchResult.id
        ),
        1
      )
    } else if (
      this.selectedSearchResults.length < this.maxSelectedSearchResults
    ) {
      this.selectedSearchResults.push(searchResult)
    }
    this.selectionChanged.emit(
      this.selectedSearchResults.length
        ? this.selectedSearchResults.map((searchResult) => searchResult.id)
        : []
    )
    this.searchEl.nativeElement.value = ''
    this.showList = false
  }

  // Click outside closes list
  @HostListener('document:click', ['$event.target'])
  clickOut(eventTarget) {
    if (!this.elementRef.nativeElement.contains(eventTarget)) {
      this.showList = false
    }
  }
}
